import * as React from "react";
import { useReviews } from "@/hooks/useReviews";
import { StoredResponseRequest, TileResponseKey, TileTypes } from "@/types/review.types";
import { useTileSearch } from "@/hooks/useTileSearch";
import { Impact, ReviewTileItem } from "@/types/tile.types";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/taskpane/components/ui/select";
import { Loader, X } from "lucide-react";
import { toast } from "sonner";
import TilesBulkActions from "./TilesBulkActions";
import { ReviewResultsStatus } from "./review-results-status";

const TileItem = React.lazy(() => import("./TileItem"));

interface TilesListProps {
  selectedQueryTerms: string[];
  documentId: string;
}

const impactOptions: { value: Exclude<Impact, "ALL">; label: string }[] = [
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

function formatQueryKey(key: string): string {
  switch (key) {
    case "negotiationPoints":
      return "points-to-negotiate";
    case "placeholders":
      return "find-placeholders";
    case "riskPoints":
      return "risk-analysis";
    default:
      return key.replace(/([A-Z])/g, "-$1").toLowerCase();
  }
}

function formatDisplayType(formattedQuery: string): string {
  return formattedQuery.replace(/-/g, " ");
}

function buildTileItem(item: TileTypes, key: TileResponseKey, index: number): ReviewTileItem {
  const formattedQuery = formatQueryKey(key);
  const displayType = formatDisplayType(formattedQuery);

  return {
    id: `${key}-${index}`,
    clause: "clause" in item ? (item.clause as string) : key,
    section: "section" in item ? (item.section as string) : undefined,
    reason: "reason" in item ? (item.reason as string) : undefined,
    analysis: "analysis" in item ? (item.analysis as string) : undefined,
    explanation: "explanation" in item ? (item.explanation as string) : undefined,
    impact: "impact" in item ? (item.impact as Impact) : undefined,
    queryType: displayType,
    query: formattedQuery,
    documentName: "documentName" in item ? (item.documentName as string) : undefined,
    originalData: item,
    originalKey: key,
  };
}

function buildReviewItems(reviewsData: {
  response?: Record<string, TileTypes[]>;
}): ReviewTileItem[] {
  if (!reviewsData?.response) return [];

  const reviewItems: ReviewTileItem[] = [];
  const entries = Object.entries(reviewsData.response) as [TileResponseKey, TileTypes[]][];
  entries.forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        reviewItems.push(buildTileItem(item, key, index));
      });
    }
  });
  return reviewItems;
}

interface ImpactFilterProps {
  impactFilter: Impact;
  setImpactFilter: (v: Impact) => void;
  reviewsData: { impact_counts?: Record<string, number> } | undefined;
}

const ImpactFilterSelect: React.FC<ImpactFilterProps> = ({
  impactFilter,
  setImpactFilter,
  reviewsData,
}) => {
  // Radix Select gets stuck if its `value` prop transitions from a string to
  // undefined (controlled → uncontrolled). Clearing via the X button causes
  // exactly that — after which subsequent selections silently fail. Force a
  // fresh Radix instance on clear by incrementing this key so Radix's internal
  // state starts from scratch. Normal value changes don't bump the key so
  // there's no remount cost for High → Medium → Low switches.
  const [resetKey, setResetKey] = React.useState(0);

  const handleClear = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setImpactFilter("ALL");
    setResetKey((k) => k + 1);
  };

  return (
    <Select
      key={resetKey}
      onValueChange={(value) => setImpactFilter(value as Impact)}
      value={impactFilter === "ALL" ? undefined : impactFilter}
    >
      <SelectTrigger className="w-[130px] px-3 text-sm font-medium">
        <div className="flex items-center justify-between w-full">
          <span>
            {impactFilter === "ALL"
              ? "Impact"
              : impactOptions.find((opt) => opt.value === impactFilter)?.label}
          </span>
          {impactFilter !== "ALL" && (
            <div
              role="button"
              tabIndex={0}
              className="ml-2 hover:bg-slate-100 rounded-full p-0.5 z-50"
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleClear(e);
                }
              }}
            >
              <X className="h-3 w-3 text-slate-500" />
            </div>
          )}
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-0">
        {impactOptions.map((opt) => {
          const count = reviewsData?.impact_counts?.[opt.value] ?? 0;

          return (
            <SelectItem
              key={opt.value}
              value={opt.value}
              className="text-sm w-full cursor-pointer pr-1"
              hideCheckmark
            >
              <div className="flex items-center">
                <span className="w-[60px] text-left">{opt.label}</span>
                <span className="font-bold ml-2">{count}</span>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

function getSelectedImpactCount(
  impactCounts: Record<string, number> | undefined,
  impactFilter: Impact
): number {
  if (!impactCounts) return 0;
  if (impactFilter === "ALL") {
    return (impactCounts.HIGH ?? 0) + (impactCounts.MEDIUM ?? 0) + (impactCounts.LOW ?? 0);
  }
  return impactCounts[impactFilter] ?? 0;
}

function buildReviewRequest(
  selectedQueryTerms: string[],
  documentId: string
): StoredResponseRequest | null {
  if (selectedQueryTerms.length === 0) return null;
  return { document_id: [documentId], query_term: selectedQueryTerms };
}

/**
 * Report a failed load of the review results.
 *
 * Without this, a failed reload leaves the panel looking simply empty —
 * indistinguishable from "the review found nothing" and from "your request
 * vanished" (QS-113). The fixed toast id means a persistent failure replaces
 * its own message rather than stacking a new one on every remount.
 */
function useReportReviewLoadFailure(failed: boolean) {
  React.useEffect(() => {
    if (!failed) return;
    toast.error("Couldn't load your review results", {
      id: "reviews-load-failed",
      description: "Please try again. If this keeps happening, close and reopen the panel.",
    });
  }, [failed]);
}

function useReviewTiles(selectedQueryTerms: string[], documentId: string) {
  const reviewRequest = React.useMemo(
    () => buildReviewRequest(selectedQueryTerms, documentId),
    [selectedQueryTerms, documentId]
  );
  const {
    data: reviewsData,
    isError: reviewsFailed,
    isPending: reviewsPending,
    isFetching: reviewsFetching,
  } = useReviews(reviewRequest);
  useReportReviewLoadFailure(reviewsFailed);
  const items = React.useMemo(
    () => buildReviewItems(reviewsData as { response?: Record<string, TileTypes[]> }),
    [reviewsData]
  );
  return { items, reviewsData, reviewsFailed, reviewsPending, reviewsFetching };
}

export const TilesList: React.FC<TilesListProps> = ({ selectedQueryTerms, documentId }) => {
  const [impactFilter, setImpactFilter] = React.useState<Impact>("ALL");
  const [isBulkOperationInProgress, setIsBulkOperationInProgress] = React.useState(false);
  const { items, reviewsData, reviewsFailed, reviewsPending, reviewsFetching } = useReviewTiles(
    selectedQueryTerms,
    documentId
  );
  const searchHook = useTileSearch({
    onError: (error) => toast.error(error),
    highlightInDocument: true,
    caseSensitive: false,
    wholeWords: false,
  });
  const filteredItems =
    impactFilter === "ALL" ? items : items.filter((item) => item.impact === impactFilter);

  return (
    <div className="flex flex-col gap-4 w-full flex-1 min-h-0">
      {items.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ImpactFilterSelect
              impactFilter={impactFilter}
              setImpactFilter={setImpactFilter}
              reviewsData={reviewsData}
            />
            <span className="text-sm">
              {getSelectedImpactCount(reviewsData?.impact_counts, impactFilter)}
            </span>
          </div>
          <TilesBulkActions
            filteredItems={filteredItems}
            documentId={documentId}
            selectedQueryTerms={selectedQueryTerms}
            onOperationStateChange={setIsBulkOperationInProgress}
            impactFilter={impactFilter}
          />
        </div>
      )}
      <div className="flex flex-col gap-3 w-full flex-1 overflow-y-auto">
        <ReviewResultsStatus
          loading={reviewsPending || reviewsFetching}
          hasItems={items.length > 0}
          hasTerms={selectedQueryTerms.length > 0}
          failed={reviewsFailed}
        />
        <React.Suspense fallback={<Loader className="animate-spin self-center" />}>
          {filteredItems.map((item) => (
            <TileItem
              key={item.id}
              item={item}
              documentId={documentId}
              isBulkOperationInProgress={isBulkOperationInProgress}
              onTileClick={(tile: ReviewTileItem) => searchHook.searchInDocumentWithTile(tile)}
            />
          ))}
        </React.Suspense>
      </div>
    </div>
  );
};

export default TilesList;
